import { ExcelGenerationValueWriteRule, RequestStatus, TrackingLevel, defaults, entities, enumerations, getAPIMethod } from 'typed/api';
import { ExtractAction, NetworkAction, action, entityActionSet, networkAction } from './utils';
import { download } from 'typed/utils';

export const
    { loadAll: loadAllForecastModelDataEntryTypes } = entityActionSet(defaults.ForecastModelDataEntryType, {
        loadAll: networkAction('LOAD_ALL_FORECAST_MODEL_DATA_ENTRY_TYPES', getAPIMethod('ForecastModelDataEntryType', 'loadAll'))
    }),

    { loadAll: loadAllForecastModelDataEntryStatuses } = entityActionSet(defaults.ForecastModelDataEntryStatus, {
        loadAll: networkAction('LOAD_ALL_FORECAST_MODEL_DATA_ENTRY_STATUSES', getAPIMethod('ForecastModelDataEntryStatus', 'loadAll'))
    }),

    forecastModels = {
        ...entityActionSet(defaults.ForecastModelReport, {
            loadAll: networkAction('LOAD_ALL_FORECAST_MODELS', getAPIMethod('ForecastModel', 'loadAll')),
            load: networkAction('LOAD_FORECAST_MODEL', getAPIMethod('ForecastModel', 'load')),
            edit: networkAction('EDIT_FORECAST_MODEL', getAPIMethod('ForecastModel', 'edit')),
            delete: networkAction('DELETE_FORECAST_MODEL', getAPIMethod('ForecastModel', 'delete')),
            voidAll: action('VOID_ALL_FORECAST_MODELS')
        }),
        // TODO how to pass additional parameters without a full copy of the method?
        add: (entity: entities.ForecastModelReport, childrenTrackingLevelID: TrackingLevel | null = null, sync = false):
        NetworkAction<'ADD_FORECAST_MODEL', { entity: entities.ForecastModelReport; entityID: number; childrenTrackingLevelID: TrackingLevel | null; }, void | number> =>
            async(dispatch) => {
                dispatch({
                    type: 'ADD_FORECAST_MODEL',
                    payload: { entityID: entity.ID, entity, childrenTrackingLevelID, status: RequestStatus.UNSYNCED }
                });

                if (!sync) {
                    return entity.ID;
                }
                else {
                    return await dispatch(networkAction(
                        'ADD_FORECAST_MODEL', { entityID: entity.ID, entity, childrenTrackingLevelID }, getAPIMethod('ForecastModel', 'add'), entity, { childrenTrackingLevelID }
                    ));
                }
            },
        addRecordModel: (entity: entities.ForecastModelReport) => networkAction(
            'ADD_RECORD_FORECAST_MODEL',
            { entity },
            getAPIMethod<entities.ForecastModelReport>('ForecastModel', 'addRecordModel'),
            entity
        ),
        aggregateData: (entityID: number, preview = true) => networkAction(
            'AGGREGATE_FORECAST_MODEL_DATA', { entityID, preview }, getAPIMethod<entities.ForecastModelAggregationReport>('ForecastModel', 'aggregateData'), entityID, { preview }
        )
    },

    forecastModelAggregationLevels = {
        ...entityActionSet(defaults.ForecastModelReport, defaults.ForecastModelAggregationLevelReport, {
            load: networkAction('LOAD_FORECAST_MODEL_AGGREGATION_LEVEL', getAPIMethod('ForecastModelAggregationLevel', 'load')),
            editAll: networkAction('EDIT_ALL_FORECAST_MODEL_AGGREGATION_LEVELS', getAPIMethod('ForecastModelAggregationLevel', 'editAll')),
            edit: networkAction('EDIT_FORECAST_MODEL_AGGREGATION_LEVEL', getAPIMethod('ForecastModelAggregationLevel', 'edit')),
            deleteAll: networkAction('DELETE_ALL_FORECAST_MODEL_AGGREGATION_LEVELS', getAPIMethod('ForecastModelAggregationLevel', 'deleteAll')),
            delete: networkAction('DELETE_FORECAST_MODEL_AGGREGATION_LEVEL', getAPIMethod('ForecastModelAggregationLevel', 'delete'))
        }),
        addAll: (parentID: number, entities: entities.ForecastModelAggregationLevelReport[], autoSubdivide = true, sync = false):
        NetworkAction<'ADD_ALL_FORECAST_MODEL_AGGREGATION_LEVELS', { parentID: number; entities: entities.ForecastModelAggregationLevelReport[]; entityIDs: number[]; autoSubdivide: boolean; }, void | number[]> =>
            async(dispatch) => {
                const entityIDs = entities.map(({ ID }) => ID);

                dispatch({
                    type: 'ADD_ALL_FORECAST_MODEL_AGGREGATION_LEVELS',
                    payload: { parentID, entityIDs, entities, autoSubdivide, status: RequestStatus.UNSYNCED }
                });

                if (!sync) {
                    return entityIDs;
                }
                else {
                    return await dispatch(networkAction(
                        'ADD_ALL_FORECAST_MODEL_AGGREGATION_LEVELS', { parentID, entityIDs, entities, autoSubdivide }, getAPIMethod<number[]>('ForecastModelAggregationLevel', 'addAll'), entities, { autoSubdivide }
                    ));
                }
            },
        add: (parentID: number, entity: entities.ForecastModelAggregationLevelReport, autoSubdivide = true, sync = false):
        NetworkAction<'ADD_FORECAST_MODEL_AGGREGATION_LEVEL', { parentID: number; entity: entities.ForecastModelAggregationLevelReport; entityID: number; autoSubdivide: boolean; }, void | number> =>
            async(dispatch) => {
                dispatch({
                    type: 'ADD_FORECAST_MODEL_AGGREGATION_LEVEL',
                    payload: { parentID, entityID: entity.ID, entity, autoSubdivide, status: RequestStatus.UNSYNCED }
                });

                if (!sync) {
                    return entity.ID;
                }
                else {
                    return await dispatch(networkAction(
                        'ADD_FORECAST_MODEL_AGGREGATION_LEVEL', { parentID, entityID: entity.ID, entity, autoSubdivide }, getAPIMethod<number>('ForecastModelAggregationLevel', 'add'), entity, { autoSubdivide }
                    ));
                }
            },
        aggregateData: (parentID: number, entityID: number, preview = true) => networkAction(
            'AGGREGATE_FORECAST_MODEL_AGGREGATION_LEVEL_DATA', { parentID, entityID, preview }, getAPIMethod<entities.ForecastModelAggregationReport>('ForecastModelAggregationLevel', 'aggregateData'), entityID, preview, {}
        )
    },

    forecastModelDataEntries = {
        ...entityActionSet(defaults.ForecastModelReport, defaults.ForecastModelAggregationLevelReport, defaults.ForecastModelDataEntry, {
            load: networkAction('LOAD_FORECAST_MODEL_DATA_ENTRY', getAPIMethod('ForecastModelDataEntry', 'load')),
            delete: networkAction('DELETE_FORECAST_MODEL_DATA_ENTRY', getAPIMethod('ForecastModelDataEntry', 'delete'))
        }),
        // TODO how to pass additional parameters without a full copy of the method?
        add: (grandParentID: number, parentID: number, entity: entities.ForecastModelDataEntry, withNotification = false, sync = false):
        NetworkAction<'ADD_FORECAST_MODEL_DATA_ENTRY', { grandParentID: number; parentID: number; entity: entities.ForecastModelDataEntry; entityID: number; withNotification: boolean; }, number> =>
            async(dispatch) => {
                dispatch({
                    type: 'ADD_FORECAST_MODEL_DATA_ENTRY',
                    payload: { grandParentID, parentID, entityID: entity.ID, entity, withNotification, status: RequestStatus.UNSYNCED }
                });

                if (!sync) {
                    return entity.ID;
                }
                else {
                    return await dispatch(networkAction(
                        'ADD_FORECAST_MODEL_DATA_ENTRY', { grandParentID, parentID, entityID: entity.ID, entity, withNotification }, getAPIMethod<number>('ForecastModelDataEntry', 'add'), entity, { withNotification }
                    ));
                }
            },
        edit: (grandParentID: number, parentID: number, entity: entities.ForecastModelDataEntry, withNotification: boolean, sync = false):
        NetworkAction<'EDIT_FORECAST_MODEL_DATA_ENTRY', { grandParentID: number; parentID: number; entity: entities.ForecastModelDataEntry; entityID: number; withNotification: boolean; }, void> =>
            async(dispatch) => {
                dispatch({
                    type: 'EDIT_FORECAST_MODEL_DATA_ENTRY',
                    payload: { grandParentID, parentID, entityID: entity.ID, entity, withNotification, status: RequestStatus.UNSYNCED }
                });

                if (!sync) {
                    return undefined;
                }
                else {
                    return await dispatch(networkAction(
                        'EDIT_FORECAST_MODEL_DATA_ENTRY', { grandParentID, parentID, entityID: entity.ID, entity, withNotification }, getAPIMethod('ForecastModelDataEntry', 'edit'), entity, { withNotification }
                    ));
                }
            },
        sendReminder: (grandParentID: number, parentID: number, entityID: number, statusID: enumerations.ForecastModelDataEntryStatus, managerNote: string) => networkAction(
            'SEND_FORECAST_MODEL_DATA_ENTRY_REMINDER', { grandParentID, parentID, entityID, statusID, managerNote }, getAPIMethod('ForecastModelDataEntry', 'sendReminder'), entityID, statusID, managerNote
        ),
        generateTemplate: (grandParentID: number, parentID: number, entityID: number, valueWriteRule: ExcelGenerationValueWriteRule | null = null, downloadable = false, filename = ''):
        NetworkAction<'GENERATE_FORECAST_MODEL_DATA_ENTRY_TEMPLATE', { grandParentID: number; parentID: number; entityID: number; valueWriteRule: ExcelGenerationValueWriteRule | null; downloadable: boolean; filename: string; }, void> =>
            async(dispatch) => {
                const templateURL = await dispatch(networkAction(
                    'GENERATE_FORECAST_MODEL_DATA_ENTRY_TEMPLATE', { grandParentID, parentID, entityID, valueWriteRule, downloadable, filename }, getAPIMethod<string>('ForecastModelDataEntry', 'generateTemplate'), entityID, { valueWriteRule }
                ));
                if (downloadable && templateURL) {
                    download(templateURL, filename);
                }
            },
        importData: (grandParentID: number, parentID: number, entityID: number, source: entities.FileTransferHelperEntity | null = null) => networkAction(
            'IMPORT_FORECAST_MODEL_DATA_ENTRY_DATA', { grandParentID, parentID, entityID, source }, getAPIMethod('ForecastModelDataEntry', 'importData'), source || {}, entityID
        ),
        touch: (grandParentID: number, parentID: number, entityID: number, statusID: enumerations.ForecastModelDataEntryStatus, responsibleUserNote: string) => networkAction(
            'TOUCH_FORECAST_MODEL_DATA_ENTRY', { grandParentID, parentID, entityID, statusID, responsibleUserNote }, getAPIMethod('ForecastModelDataEntry', 'touch'), entityID, statusID, responsibleUserNote
        )
    },


    forecastModelRecordDatas = {
        ...entityActionSet(defaults.ForecastModelRecordDataReport, {
            loadAll: networkAction('LOAD_ALL_FORECAST_MODEL_RECORD_DATA', getAPIMethod('ForecastModelRecordData', 'loadAll')),
            add: networkAction('ADD_FORECAST_MODEL_RECORD_DATA', getAPIMethod('ForecastModelRecordData', 'add')),
            edit: networkAction('EDIT_FORECAST_MODEL_RECORD_DATA', getAPIMethod('ForecastModelRecordData', 'edit')),
            delete: networkAction('DELETE_FORECAST_MODEL_RECORD_DATA', getAPIMethod('ForecastModelRecordData', 'delete')),
            voidAll: action('VOID_ALL_FORECAST_MODEL_RECORD_DATA')
        }),
        import: (entityID: number) => networkAction('IMPORT_FORECAST_MODEL_RECORD_DATA', { entityID }, getAPIMethod('ForecastModelRecordData', 'import'), entityID),
        loadResultSlice: (entityID: number, options: { startID?: number; maxCount?: number; slicingOrder?: number;} = {}) =>
            networkAction(
                'LOAD_FORECAST_MODEL_RECORD_DATA_RESULT_SLICE',
                { entityID, options },
                getAPIMethod<entities.NetworkPaginationSlice<entities.AccountingData>>('AccountingData', 'searchSlice'),
                { ForecastModelRecordDataID: entityID },
                options
            )
    },

    { loadAll: loadAllForecastModelRecordDataImportPeriodTypes } = entityActionSet(defaults.ForecastModelRecordDataImportPeriodType, {
        loadAll: networkAction('LOAD_ALL_FORECAST_MODEL_RECORD_DATA_IMPORT_PERIOD_TYPES', getAPIMethod('ForecastModelRecordDataImportPeriodType', 'loadAll'))
    }),

    forecastModelRecordDataFiles = {
        ...entityActionSet(defaults.ForecastModelRecordDataReport, defaults.ForecastModelRecordDataFileReport, {
            add: networkAction('ADD_FORECAST_MODEL_RECORD_DATA_FILE', getAPIMethod('ForecastModelRecordDataFile', 'add')),
            edit: networkAction('EDIT_FORECAST_MODEL_RECORD_DATA_FILE', getAPIMethod('ForecastModelRecordDataFile', 'edit')),
            delete: networkAction('DELETE_FORECAST_MODEL_RECORD_DATA_FILE', getAPIMethod('ForecastModelRecordDataFile', 'delete'))
        }),
        uploadFile: (file: entities.FileTransferHelperEntity, options: { recordID?: number | null; fileID?: number | null;} = {}) =>
            networkAction(
                'UPLOAD_FORECAST_MODEL_RECORD_DATA_FILE',
                { file, parentID: options.recordID, entityID: options.fileID },
                getAPIMethod<entities.ForecastModelRecordDataFileReport>('ForecastModelRecordDataFile', 'uploadFile'),
                file,
                options
            )
    },

    forecastModelRecordDataActualSplitRanges = entityActionSet(
        defaults.ForecastModelRecordDataReport,
        defaults.ForecastModelRecordDataFileReport,
        defaults.ForecastModelRecordDataActualSplitRange,
        {
            add: networkAction('ADD_FORECAST_MODEL_RECORD_DATA_ACTUAL_SPLIT_RANGE', getAPIMethod('ForecastModelRecordDataActualSplitRange', 'add')),
            edit: networkAction('EDIT_FORECAST_MODEL_RECORD_DATA_ACTUAL_SPLIT_RANGE', getAPIMethod('ForecastModelRecordDataActualSplitRange', 'edit')),
            delete: networkAction('DELETE_FORECAST_MODEL_RECORD_DATA_ACTUAL_SPLIT_RANGE', getAPIMethod('ForecastModelRecordDataActualSplitRange', 'delete'))
        }
    ),


    { loadAll: loadAllVATPeriodTypes } = entityActionSet(defaults.VATPeriodType, {
        loadAll: networkAction('LOAD_ALL_VAT_PERIOD_TYPES', getAPIMethod('VATPeriodType', 'loadAll'))
    }),

    cashflowForecastSettings = { // TODO entityActionSet for ID-less singleton entities
        load: () => networkAction(
            'LOAD_CASHFLOW_FORECAST_SETTINGS', {}, getAPIMethod<entities.CashflowForecastSettingsReport>('CashflowForecastSettings', 'load')
        ),
        edit: (settings: entities.CashflowForecastSettings) => networkAction(
            'EDIT_CASHFLOW_FORECAST_SETTINGS', { entity: settings }, getAPIMethod('CashflowForecastSettings', 'edit'), settings
        ),
        recalculate: () => networkAction(
            'RECALCULATE_CASHFLOW_FORECAST', {}, getAPIMethod('CashflowForecastSettings', 'recalculate')
        )
    },

    cashflowForecastIndicatorConnections = entityActionSet(defaults.CashflowForecastIndicatorConnectionReport, {
        add: networkAction('ADD_CASHFLOW_FORECAST_INDICATOR_CONNECTION', getAPIMethod('CashflowForecastIndicatorConnection', 'add')),
        edit: networkAction('EDIT_CASHFLOW_FORECAST_INDICATOR_CONNECTION', getAPIMethod('CashflowForecastIndicatorConnection', 'edit')),
        delete: networkAction('DELETE_CASHFLOW_FORECAST_INDICATOR_CONNECTION', getAPIMethod('CashflowForecastIndicatorConnection', 'delete'))
    }),

    cashflowForecastIndicatorDistributionEntries = entityActionSet(defaults.CashflowForecastIndicatorConnectionReport, defaults.CashflowForecastIndicatorDistributionEntry, {
        addAll: networkAction('ADD_ALL_CASHFLOW_FORECAST_INDICATOR_DISTRIBUTION_ENTRY', getAPIMethod('CashflowForecastIndicatorDistributionEntry', 'addAll')),
        editAll: networkAction('EDIT_ALL_CASHFLOW_FORECAST_INDICATOR_DISTRIBUTION_ENTRY', getAPIMethod('CashflowForecastIndicatorDistributionEntry', 'editAll')),
        deleteAll: networkAction('DELETE_ALL_CASHFLOW_FORECAST_INDICATOR_DISTRIBUTION_ENTRY', getAPIMethod('CashflowForecastIndicatorDistributionEntry', 'deleteAll'))
    }),

    loadCashflowForecastReport = ({ maxReceivablesCount }: { maxReceivablesCount?: number; } = {}) =>
        networkAction('LOAD_CASHFLOW_FORECAST_REPORT', {}, getAPIMethod<entities.CashflowForecastDataReport>('CashflowForecastSettings', 'loadDataReport'), { maxReceivablesCount });

export type ForecastsAction = ExtractAction<
| typeof loadAllForecastModelDataEntryTypes
| typeof loadAllForecastModelDataEntryStatuses
| typeof forecastModels
| typeof forecastModelAggregationLevels
| typeof forecastModelDataEntries
| typeof forecastModelRecordDatas
| typeof loadAllForecastModelRecordDataImportPeriodTypes
| typeof forecastModelRecordDataFiles
| typeof forecastModelRecordDataActualSplitRanges
| typeof loadAllVATPeriodTypes
| typeof cashflowForecastSettings
| typeof cashflowForecastIndicatorConnections
| typeof cashflowForecastIndicatorDistributionEntries
| typeof loadCashflowForecastReport
>;
